import React, { useActionState, useMemo, useRef } from 'react';
import { Dialog, DialogProps, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { Storyboard } from '../../../app/entities';

interface CreateStoryboardModalProps extends DialogProps {
  projectId: string;
}

export const CreateStoryboardModal: React.FC<CreateStoryboardModalProps> = ({
  onCancel,
  projectId,
                                                                            }) => {
  const { t } = useTranslation('video');
  const ref = useRef<HTMLFormElement>(null);

  const schema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .required(t('common:fieldRequired'))
          .max(24, t('common:fieldMax', { max: 24 }))
      }),
    [t],
  );

  const [state, formAction, isPending] = useActionState<any, any>(
    async (state, formData) => {
      const data = Object.fromEntries(formData);
      let result;

      try {
        result = await schema.validate(data, { abortEarly: false });
      } catch (e) {
        const errors = {};

        e.inner.forEach((error) => {
          errors[error.path] = error.message;
        });

        return {
          errors,
        };
      }

      try {
        await createStoryboard(result);
        onCancel();
      } catch (e) {
        console.error(e);
      }

      return {
        values: data,
        errors: {},
      }
    },
    {
      values: {
        name: "New storyboard",
      },
      errors: {},
    },
  );

  const createStoryboard = async (result: any) => {
    const storyboard = new Storyboard();
    storyboard.projectId = projectId;
    storyboard.name = result.name;
    return storyboard.save();
  }

  return (
    <Dialog
      title={'Create storyboard'}
      onCancel={onCancel}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text={t('common:cancel')} onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          text={t('common:submit')}
          isLoading={isPending}
          type="submit"
          form="add-tag-form"
          onClick={() => ref?.current?.requestSubmit()}
        />,
      ]}
    >
      <form ref={ref} action={formAction}>
        <TextInput
          name={'name'}
          label={'Name'}
          defaultValue={state.values.name}
          errorMessage={state.errors.name}
          placeholder={'Storyboard name'}
          autoFocus
          required
        />
      </form>
    </Dialog>
  );
};
